import { useMaps } from "../useMaps/useMaps";

/**
 * Custom hook for managing city autocompletion.
 *
 * This hook integrates with a city search service and manages the state of cities
 * in the local application, ensuring synchronization between user input and available
 * city data.
 *
 * @param {Object} params - The parameters for the hook.
 * @param {Function} params.update - Function to update the parent state with new city data.
 * @param {string} params.city - The current city entered by the user.
 * @param {string} params.regionCode - The region code used for filtering city searches.
 * @param {Object} params.cities - Object containing cities grouped by region codes.
 * @param {Function} params.addCity - Function to add a city to the local state.
 * @returns {Object} An object containing handlers for managing city input and autocomplete.
 */
export const useCityAutocomplete = ({
  update,
  city,
  regionCode,
  cities,
  addCity,
}) => {
  const { searchCities } = useMaps();

  /**
   * Fetches cities matching the query from the search service and updates the local state.
   *
   * @param {string} query - The partial or full city name entered by the user.
   * @returns {Promise<void>} Resolves after fetching and updating cities.
   */
  const handleGetCities = async (query) => {
    const results = await searchCities(query, regionCode);

    results.forEach((c) => {
      if (!cities[regionCode]?.includes(c)) {
        addCity(c, regionCode);
      }
    });
  };

  /**
   * Checks if the current city is already in the local state for the given region.
   *
   * @returns {boolean} `true` if the city exists in the local state; otherwise, `false`.
   */
  const isCityInLocalState = () =>
    cities[regionCode]?.some((c) => c.toLowerCase() === city?.toLowerCase());

  /**
   * Handles the blur event on the city input field.
   * If the city is not in the local state, it resets the city field.
   */
  const handleCityBlur = () => {
    if (!isCityInLocalState()) {
      update({ city: "" });
    }
  };

  /**
   * Handles changes to the city text input field.
   * Updates the parent state and triggers a search for cities matching the input value.
   *
   * @param {string} value - The current value of the city input field.
   */
  const handleCityTextChange = (value) => {
    update({ city: value });

    if (value && addCity) {
      handleGetCities(value);
    }
  };

  return {
    handleGetCities,
    isCityInLocalState,
    handleCityBlur,
    handleCityTextChange,
  };
};
